/**
 * Agora Provider Implementation.
 *
 * This provider uses Agora's H.264 SEI approach
 * to transport animation data.
 *
 * Key differences from LiveKit:
 * - Uses native SEI events instead of RTCRtpScriptTransform
 * - No ALR (Application-Level Redundancy) needed - Agora handles reliability
 * - Simpler data extraction - uses SEI header parsing
 *
 * @packageDocumentation
 */

import { BaseProvider } from '../base/BaseProvider';
import type { RTCConnectionConfig, AgoraConnectionConfig } from '../../types';
import { isAgoraConfig, ConnectionState } from '../../types';
import type {
  AnimationTrackCallbacks,
  AudioTrackCallbacks,
} from '../../core/types';
import { SEIExtractor } from './SEIExtractor';
import { logger } from '../../utils';
import type {
  AgoraClient,
  AgoraLocalAudioTrack,
  AgoraRemoteAudioTrack,
  AgoraRemoteVideoTrack,
  AgoraUID,
} from './types';

/**
 * Agora Provider options.
 * @internal Reserved for future use
 */
export type AgoraProviderOptions = Record<string, never>;

/**
 * Remote user info with tracks
 * @internal
 */
interface RemoteUserInfo {
  videoTrack?: AgoraRemoteVideoTrack;
  audioTrack?: AgoraRemoteAudioTrack;
}

/**
 * Agora Provider.
 *
 * Implements RTCProvider interface for Agora platform.
 * Uses native SEI events to receive animation data from H.264 video tracks.
 *
 * @example
 * ```typescript
 * import { AvatarPlayer } from '@spatialwalk/avatarkit-rtc';
 * import { AgoraProvider } from '@spatialwalk/avatarkit-rtc/providers/agora';
 *
 * const provider = new AgoraProvider();
 * const player = new AvatarPlayer(provider, renderer);
 *
 * await player.connect({
 *   appId: 'your-agora-app-id',
 *   channel: 'your-channel',
 *   token: 'your-token',
 * });
 * ```
 */
export class AgoraProvider extends BaseProvider {
  /** Provider name identifier */
  readonly name = 'agora';

  /** @internal */
  private client: AgoraClient | null = null;
  /** @internal Dynamic SDK - type is any due to dynamic import */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private agoraSDK: any = null;

  // Animation track subscription (stored for cleanup, actual callbacks handled by SEIExtractor)
  /** @internal */
  private _animationCallbacks: AnimationTrackCallbacks | null = null;
  /** @internal */
  private seiExtractor: SEIExtractor | null = null;
  /** @internal */
  private remoteUsers: Map<AgoraUID, RemoteUserInfo> = new Map();
  /** @internal */
  private videoContainers: Map<AgoraUID, HTMLDivElement> = new Map();

  // Audio track subscription
  /** @internal */
  private audioCallbacks: AudioTrackCallbacks | null = null;
  /** @internal */
  private localAudioTrack: AgoraLocalAudioTrack | null = null;
  /** @internal */
  private remoteAudioTracks: Map<AgoraUID, AgoraRemoteAudioTrack> = new Map();

  // Debug mode
  /** @internal */
  private debugLogging = false;

  constructor(_options: AgoraProviderOptions = {}) {
    super();
  }

  /**
   * Enable or disable debug logging.
   * @param enabled - Whether to enable debug logging
   */
  setDebugLogging(enabled: boolean): void {
    this.debugLogging = enabled;
    if (this.seiExtractor) {
      this.seiExtractor.setDebugLogging(enabled);
    }
  }

  /**
   * Load Agora SDK dynamically.
   * @internal
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async loadSDK(): Promise<any> {
    if (this.agoraSDK) {
      return this.agoraSDK;
    }

    try {
      // Direct dynamic import - Vite will handle this properly with optimizeDeps
      // Using 'any' type due to dynamic import type inference limitations
      const sdk = await import('agora-rtc-sdk-ng');
      // Agora SDK exports default as the main AgoraRTC object
      this.agoraSDK = sdk.default ?? sdk;
      return this.agoraSDK;
    } catch (error) {
      logger.error('Agora', 'Failed to load SDK:', error);
      throw new Error(
        '❌ Failed to load agora-rtc-sdk-ng.\n' +
          'Please ensure it is installed: pnpm add agora-rtc-sdk-ng',
      );
    }
  }

  async connect(config: RTCConnectionConfig): Promise<void> {
    if (!isAgoraConfig(config)) {
      throw new Error(
        'AgoraProvider requires appId and channel in connection config',
      );
    }

    const agoraConfig: AgoraConnectionConfig = config;

    const AgoraRTC = await this.loadSDK();

    // Enable SEI reception (required for animation data)
    AgoraRTC.setParameter('ENABLE_VIDEO_SEI', true);
    AgoraRTC.setParameter('ENABLE_FULL_LINK_AV_SYNC', true);

    this.client = AgoraRTC.createClient({
      mode: 'rtc',
      codec: 'h264', // Required for SEI support
    });

    this.setConnectionState('connecting');

    // Setup event listeners
    this.setupEventListeners();

    try {
      await this.client.join(
        agoraConfig.appId,
        agoraConfig.channel,
        agoraConfig.token || null,
        agoraConfig.uid ?? null,
      );

      this.setConnectionState('connected');
      this.emit('connected');
    } catch (error) {
      this.setConnectionState('failed');
      this.emit('error', error as Error);
      throw error;
    }
  }

  /**
   * Setup Agora client event listeners
   * @internal
   */
  private setupEventListeners(): void {
    // Connection state changed
    this.client.on(
      'connection-state-change',
      (curState: string, _revState: string) => {
        const mapConnectionState = (state: string): ConnectionState => {
          switch (state) {
            case 'DISCONNECTED':
            case 'DISCONNECTING':
              return ConnectionState.Disconnected;
            case 'CONNECTING':
              return ConnectionState.Connecting;
            case 'CONNECTED':
              return ConnectionState.Connected;
            case 'RECONNECTING':
              return ConnectionState.Reconnecting;
            default:
              return ConnectionState.Failed;
          }
        };

        this.setConnectionState(mapConnectionState(curState));

        if (curState === 'CONNECTED') {
          this.emit('connected');
        } else if (curState === 'DISCONNECTED') {
          this.cleanup();
          this.emit('disconnected');
        }
      },
    );

    // User published (remote user joined and published tracks)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.client.on(
      'user-published',
      async (user: any, mediaType: 'audio' | 'video') => {
        if (this.debugLogging) {
          logger.info(
            'Agora',
            `User published: ${user.uid}, mediaType=${mediaType}`,
          );
        }

        try {
          // Subscribe to the track
          await this.client.subscribe(user, mediaType);

          if (mediaType === 'video') {
            if (user.videoTrack) {
              this.handleVideoTrack(user, user.videoTrack);
            } else {
              logger.warn(
                'Agora',
                `Video track is null after subscribe for user ${user.uid}`,
              );
            }
          } else if (mediaType === 'audio') {
            if (user.audioTrack) {
              this.handleAudioTrack(user, user.audioTrack);
            } else {
              logger.warn(
                'Agora',
                `Audio track is null after subscribe for user ${user.uid}`,
              );
            }
          }
        } catch (error) {
          logger.error(
            'Agora',
            `Failed to subscribe to ${mediaType} from ${user.uid}:`,
            error,
          );
          this.emit('error', error as Error);
        }
      },
    );

    // User unpublished (remote user stopped publishing)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.client.on(
      'user-unpublished',
      (user: any, mediaType: 'audio' | 'video') => {
        if (this.debugLogging) {
          logger.info(
            'Agora',
            `User unpublished: ${user.uid}, mediaType=${mediaType}`,
          );
        }

        if (mediaType === 'video') {
          this.removeVideoContainer(user.uid);
          this.remoteUsers.delete(user.uid);
        } else if (mediaType === 'audio') {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const track = this.remoteAudioTracks.get(user.uid) as any;
          if (track) {
            track.stop();
            this.remoteAudioTracks.delete(user.uid);
          }

          if (this.audioCallbacks) {
            this.audioCallbacks.onAudioLost?.(user);
          }
        }
      },
    );

    // User joined
    this.client.on('user-joined', (user: any) => {
      logger.info('Agora', `User joined: ${user.uid}`);
    });

    // User left
    this.client.on('user-left', (user: any, reason: string) => {
      logger.info('Agora', `User left: ${user.uid}, reason: ${reason}`);
      this.remoteUsers.delete(user.uid);
      const audioTrack = this.remoteAudioTracks.get(user.uid);
      if (audioTrack) {
        audioTrack.stop();
        this.remoteAudioTracks.delete(user.uid);
      }
    });

    // Error
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.client.on('exception', (error: any) => {
      logger.error('Agora', 'Exception:', error);
      this.emit('error', new Error(error.msg || String(error)));
    });
  }

  /**
   * Handle video track from remote user.
   * Sets up SEI event listeners and plays to hidden container.
   * @internal
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleVideoTrack(user: any, track: any): void {
    if (this.debugLogging) {
      logger.info('Agora', `Handling video track from ${user.uid}`);
    }

    // Listen for SEI data (animation frames)
    // Support both event names for SDK compatibility
    const seiHandler = (seiData: Uint8Array) => {
      if (this.seiExtractor) {
        this.seiExtractor.handleSEIData(seiData);
      }
    };

    // Try primary event name
    track.on('sei-received', seiHandler);
    // Also try alternative event name used in some SDK versions
    track.on('video-sei-received', seiHandler);

    // Play video to hidden element to ensure data flows
    // This is required for SEI events to be received
    const container = document.createElement('div');
    container.style.display = 'none';
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.id = `agora-video-${user.uid}`;
    document.body.appendChild(container);
    this.videoContainers.set(user.uid, container);
    track.play(container);

    if (this.debugLogging) {
      logger.info(
        'Agora',
        `Video track playing, SEI listeners attached for user ${user.uid}`,
      );
    }

    // Store track reference
    const existingUser = this.remoteUsers.get(user.uid);
    if (existingUser) {
      existingUser.videoTrack = track;
    } else {
      this.remoteUsers.set(user.uid, { videoTrack: track });
    }
  }

  /**
   * Handle audio track from remote user.
   * @internal
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleAudioTrack(user: any, track: any): void {
    if (this.debugLogging) {
      logger.info('Agora', `Handling audio track from ${user.uid}`);
    }

    // Play audio
    track.play();
    this.remoteAudioTracks.set(user.uid, track);

    if (this.debugLogging) {
      logger.info('Agora', `Audio track playing for user ${user.uid}`);
    }

    if (this.audioCallbacks) {
      this.audioCallbacks.onAudioReceived?.(user);
    }
  }

  /**
   * Remove video container for a user.
   * @internal
   */
  private removeVideoContainer(uid: number): void {
    const container = this.videoContainers.get(uid);
    if (container) {
      container.remove();
      this.videoContainers.delete(uid);
    }
  }

  async disconnect(): Promise<void> {
    this.cleanup();
    if (this.client) {
      await this.client.leave();
      this.client = null;
    }
    this.setConnectionState('disconnected');
    this.emit('disconnected');
  }

  getConnectionState(): string {
    if (!this.client) {
      return 'disconnected';
    }
    return this.connectionState;
  }

  /** @internal */
  async subscribeAnimationTrack(
    callbacks: AnimationTrackCallbacks,
  ): Promise<void> {
    this._animationCallbacks = callbacks;

    // Create SEI extractor
    this.seiExtractor = new SEIExtractor();
    this.seiExtractor.setDebugLogging(this.debugLogging);
    this.seiExtractor.initialize(callbacks);

    // If already connected, check for existing remote video tracks
    if (this.client) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const remoteUsers = (this.client as any).remoteUsers;
      for (const user of remoteUsers) {
        if (user.videoTrack) {
          // Subscribe to SEI events with both event names for compatibility
          const seiHandler = (seiData: Uint8Array) => {
            if (this.seiExtractor) {
              this.seiExtractor.handleSEIData(seiData);
            }
          };
          user.videoTrack.on('sei-received', seiHandler);
          user.videoTrack.on('video-sei-received', seiHandler);
        }
      }
    }
  }

  /** @internal */
  async unsubscribeAnimationTrack(): Promise<void> {
    this._animationCallbacks = null;
    if (this.seiExtractor) {
      this.seiExtractor.dispose();
      this.seiExtractor = null;
    }

    // Remove SEI listeners from remote tracks
    for (const [_uid, user] of this.remoteUsers) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const videoTrack = user.videoTrack as any;
      if (videoTrack) {
        videoTrack.off('sei-received');
        videoTrack.off('video-sei-received');
      }
    }

    // Remove video containers
    for (const [uid] of this.videoContainers) {
      this.removeVideoContainer(uid as number);
    }

    this.remoteUsers.clear();
  }

  /** @internal */
  async subscribeAudioTrack(callbacks: AudioTrackCallbacks): Promise<void> {
    this.audioCallbacks = callbacks;
    // Audio tracks are automatically handled in user-published event
  }

  /** @internal */
  async unsubscribeAudioTrack(): Promise<void> {
    this.audioCallbacks = null;
    this.remoteAudioTracks.forEach((track) => {
      track.stop();
    });
    this.remoteAudioTracks.clear();
  }

  async publishAudioTrack(track?: MediaStreamTrack): Promise<void> {
    if (!this.client) {
      throw new Error('Not connected to channel');
    }

    const AgoraRTC = await this.loadSDK();

    if (track) {
      // Create local audio track from provided MediaStreamTrack
      this.localAudioTrack = AgoraRTC.createCustomAudioTrack({
        mediaStreamTrack: track,
      });
    } else {
      // Create local audio track from microphone
      this.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
        encoderConfig: 'music_standard',
        AEC: true,
        ANS: true,
        AGC: true,
      });
    }

    // Publish the track
    await this.client.publish(this.localAudioTrack);
  }

  async unpublishAudioTrack(): Promise<void> {
    if (!this.client || !this.localAudioTrack) {
      return;
    }

    await this.client.unpublish(this.localAudioTrack);
    this.localAudioTrack.close();
    this.localAudioTrack = null;
  }

  /**
   * Get the native Agora RTC Client instance.
   *
   * Allows advanced users to access Agora-specific features
   * not exposed through the unified API.
   *
   * @returns The Agora IAgoraRTCClient instance, or null if not connected
   *
   * @example
   * ```typescript
   * const client = provider.getNativeClient();
   * if (client) {
   *   // Access Agora-specific features
   *   console.log('Connection state:', client.connectionState);
   * }
   * ```
   */
  getNativeClient(): AgoraClient | null {
    return this.client;
  }

  /**
   * Cleanup resources
   * @internal
   */
  private cleanup(): void {
    // Cleanup remote audio tracks
    this.remoteAudioTracks.forEach((track) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (track as any).stop();
    });
    this.remoteAudioTracks.clear();

    // Cleanup remote video tracks and SEI listeners
    for (const [_uid, user] of this.remoteUsers) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const videoTrack = user.videoTrack as any;
      if (videoTrack) {
        videoTrack.off('sei-received');
        videoTrack.off('video-sei-received');
      }
    }
    this.remoteUsers.clear();

    // Cleanup video containers
    for (const [uid] of this.videoContainers) {
      this.removeVideoContainer(uid as number);
    }

    // Cleanup SEI extractor
    if (this.seiExtractor) {
      this.seiExtractor.dispose();
      this.seiExtractor = null;
    }

    // Cleanup local audio track
    if (this.localAudioTrack) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this.localAudioTrack as any).close();
      this.localAudioTrack = null;
    }
  }
}
